import { Box, Card, Collapse, Divider, Link, Text } from '@ksyos/design-system';
import { useState } from 'react';

type InvoicingNotContractedCareProps = {
    insurerName?: string | null;
};

export function InvoicingNotContractedCare(props: Readonly<InvoicingNotContractedCareProps>) {
    const { insurerName } = props;
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <Card>
                <Card.Body>
                    <Box maxWidth={612}>
                        {`Volgens onze gegevens ben jij verzekerd bij ${insurerName}. Ksyos heeft geen contract met deze zorgverzekeraar. Lees hieronder wat jij moet doen.`}
                    </Box>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={6}
                        maxWidth={692}
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="email-post"
                                src="/assets/images/somnology/insurance/Download_rekening_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text fontWeight="bold">1. Dien de factuur in bij je zorgverzekeraar</Text>
                                <Text pl={4}>Dit kan meestal simpel via hun website of app.</Text>
                            </Box>
                        </Box>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="email-post"
                                src="/assets/images/somnology/insurance/Declareren_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text fontWeight="bold">
                                    2. Bekijk het vergoedingsoverzicht dat je ontvangt van je zorgverzekeraar
                                </Text>
                                <Text pl={4}>
                                    Hierop zie je welk bedrag je terugkrijgt en welk deel onder je eigen risico valt.
                                    Ksyos gebruikt dit overzicht om te zien hoeveel jij nog moet betalen.
                                </Text>
                            </Box>
                        </Box>

                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="factuur"
                                src="/assets/images/somnology/insurance/Upload_doc.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text fontWeight="bold">3. Upload het overzicht in je Ksyos factuurdossier</Text>
                                <Text pl={4}>
                                    Dit kan via de knop &apos;Bekijk online het factuurdossier&apos; in de e-mail van
                                    Ksyos. Ga bij een papieren factuur naar www.factuurinzien.nl. Je toegangscode en
                                    pincode staan in de bijlage.
                                </Text>
                            </Box>
                        </Box>

                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="vergoeding"
                                src="/assets/images/somnology/insurance/Vergoeding_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text fontWeight="bold">4. Betaal aan Ksyos</Text>
                                <Text pl={4}>
                                    Betaal het bedrag dat je van je zorgverzekeraar vergoed krijgt plus je openstaande
                                    eigen risico. De rest neemt Ksyos op zich. Zorg van Ksyos kost je dus nooit meer dan
                                    je eigen risico. Je hebt 30 dagen om het te regelen en te betalen, dus wacht niet te
                                    lang.
                                </Text>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        maxWidth={612}
                        mt={8}
                    >
                        <Collapse isOpen={isOpen}>
                            <Text pb={6}>
                                Een slaaponderzoek en consult kosten ongeveer €900. Een eventuele behandeling komt daar
                                nog bij. Wat jij uiteindelijk betaalt, hangt af van de vergoeding van je zorgverzekeraar
                                en je openstaande eigen risico. Twijfel je hoeveel eigen risico je nog hebt? Check dit
                                eenvoudig bij je zorgverzekeraar. Zo kom je niet voor verrassingen te staan.
                            </Text>
                            <Text pb={6}>
                                Hieronder zie je een voorbeeld van de kosten. Let op: De kosten verschillen per persoon
                                en behandeling.
                            </Text>
                            <Box
                                backgroundColor="neutral50"
                                padding={4}
                            >
                                <Box
                                    pl={4}
                                    pr={4}
                                >
                                    <Text
                                        fontWeight="bold"
                                        pb={4}
                                    >
                                        Voorbeeld
                                    </Text>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                    >
                                        <Text fontWeight="bold">Vergoedingsoverzicht van een zorgverzekeraar</Text>
                                        <Text>Totaal: €800 </Text>
                                        <Text pl={4}>Vergoed: €480</Text>
                                        <Text pl={4}>Niet vergoed: €320</Text>
                                        <Text pl={4}>Eigen risico: €235</Text>
                                        <Text
                                            as="span"
                                            pl={4}
                                        >
                                            Jij ontvangt:{' '}
                                            <Text
                                                as="span"
                                                color="success"
                                                fontWeight="bold"
                                            >
                                                €245
                                            </Text>
                                        </Text>
                                        <Divider spacing="small" />

                                        <Text fontWeight="bold"> Jij betaalt aan Ksyos</Text>
                                        <Text>Ontvangen bedrag: €245 </Text>
                                        <Text>Eigen risico: €235 </Text>
                                    </Box>
                                    <Box maxWidth={180}>
                                        <Text
                                            lineHeight={3}
                                            textAlign="right"
                                        >
                                            +
                                        </Text>
                                    </Box>
                                    <Box maxWidth={172}>
                                        <Divider spacing="small" />
                                    </Box>
                                </Box>
                                <Box
                                    backgroundColor="white"
                                    padding={2}
                                >
                                    <Text fontWeight="bold">Betalen: €480</Text>
                                </Box>
                            </Box>

                            <Text
                                fontSize={'sm'}
                                pt={6}
                            >
                                Zie voor meer informatie onze{' '}
                                <Link
                                    color="secondary"
                                    href="https://ksyos.nl/algemene-voorwaarden/"
                                    openInNewTab
                                    variant="externalLink"
                                >
                                    algemene voorwaarden.
                                </Link>
                            </Text>
                        </Collapse>
                        <Divider spacing="small" />
                        <Link
                            color="subtle"
                            fontWeight="normal"
                            iconLeft={isOpen ? 'caret-up' : 'caret-down'}
                            onClick={() => setIsOpen(!isOpen)}
                            size="xs"
                            style={{ marginBlock: 'var(--kds-spacing-2)' }}
                            variant="button"
                        >
                            {isOpen
                                ? 'Lees minder over de berekening van de kosten'
                                : 'Lees meer over de berekening van de kosten'}
                        </Link>
                    </Box>
                </Card.Body>
            </Card>
        </>
    );
}
